import { isRef, nextTick, Ref, toValue } from 'vue';

import { MoveSortableElementsOptions } from './useSortable.types';

/**
 * Move sortable elements in the list
 * @param list List of sortable elements
 * @param from Original element position
 * @param to New element position
 */
export function moveSortableElements<ListItem>({ list, from, to }: MoveSortableElementsOptions<ListItem>): void {
  if (from === to) return;

  const _listIsRef = isRef(list);
  // When the list is a ref, make a shallow copy of it to avoid repeatedly triggering side effects when moving elements
  const listCopy = _listIsRef ? [...toValue(list)] : toValue(list);

  if (to >= 0 && to < listCopy.length) {
    const element = listCopy.splice(from, 1)[0];
    nextTick(() => {
      listCopy.splice(to, 0, element);
      // When list is ref, assign listCopy to list.value
      if (_listIsRef) (list as Ref).value = listCopy;
    });
  }
}

/**
 * Create a ghost element for dragging, which is the element that follows the cursor while dragging
 * @param e Drag event
 * @param ghostClass Custom classes to be added to the ghost element
 * @returns Ghost element and cleanup function
 */
export function createGhostElement(e: DragEvent, ghostClass: string[]) {
  const ghost = getValidDragTarget(e.target as HTMLElement).cloneNode(true) as HTMLElement;

  ghost.classList.add(...ghostClass);

  ghost.style.position = 'absolute';
  ghost.style.top = '-9999px';
  ghost.style.listStyle = 'none';
  ghost.setAttribute('data-test', `${ghost.dataset.test}|ghost`);
  ghost.removeAttribute('draggable');

  document.body.appendChild(ghost);

  e.dataTransfer?.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);

  function cleanupGhostElement() {
    requestAnimationFrame(() => {
      document.body.removeChild(ghost);
    });
  }

  return {
    ghost,
    cleanupGhostElement,
  };
}

/**
 * Get the valid draggable target element
 * @param element
 * @returns Draggable element
 */
export function getValidDragTarget(element: HTMLElement | null) {
  const dragElement = element?.closest<HTMLElement>('[draggable=true]');
  if (!dragElement) throw new Error('Draggable element not found');
  return dragElement;
}
